import { Vehicle } from '@/types/types';
import { SvelteDate } from 'svelte/reactivity';
import type { Job, JobsMap, RelatedJobMap, SchedularEmployeeAssignment, SchedularSiteVisit, SchedularVehicle, SchedularVehicles } from './types';

export const buildVehicles: (vehicles: SchedularVehicle[]) => SchedularVehicles = (vehicles) => {
    const vehiclesMapped: SchedularVehicle[] = [];
    for (let i = 0; i < vehicles.length; i++) {
        vehiclesMapped.push({
            ...vehicles[i],
            offset: i,
            overlappingJobIds: [],
        });
    }
    return vehiclesMapped;
};

// Map job types to event color names (matches CSS variables in app.css)
export const getColorForJobType = (jobType: string | null): string | null => {
    if (!jobType) return null;
    const type = jobType.toLowerCase();

    const colorMap: Record<string, string> = {
        erect: 'green',
        'specialist erect': 'emerald',
        'mobile tower': 'blue',
        'suspended scaffold': 'violet',
        shoring: 'amber',
        'strike - stack only': 'red',
        'full strike': 'rose',
        'partial strike': 'pink',
        modify: 'yellow',
        extension: 'lime',
        relocation: 'cyan',
        inspection: 'indigo',
        maintenance: 'orange',
        'handrail / edge protection': 'teal',
        'netting / sheeting': 'gray',
        'loading / unloading': 'magenta',
        'temporary roof': 'purple',
    };

    return colorMap[type] ?? null;
};

// Legacy function for backwards compatibility - converts color name to approximate hue
export const getHueForJobType = (jobType: string | null): number | null => {
    const color = getColorForJobType(jobType);
    if (!color) return null;

    // Map color names to approximate HSL hue values
    const hueMap: Record<string, number> = {
        green: 152,
        emerald: 160,
        blue: 250,
        violet: 285,
        amber: 70,
        red: 25,
        rose: 10,
        pink: 350,
        yellow: 85,
        lime: 125,
        cyan: 200,
        indigo: 270,
        orange: 55,
        teal: 180,
        gray: 0,
        magenta: 320,
        purple: 300,
    };

    return hueMap[color] ?? null;
};

export const loadJob: (
    job: any,
    vehicles: any,
) => {
    id: string;
    title: string;
    site: any;
    start: any;
    duration: number;
    modified: boolean;
    hue: number;
    notes: string;
    description: string;
    site_name: any;
    related_job_ids: string[];
    vehicle: Vehicle;
    employeeAssignments: SchedularEmployeeAssignment[];
    duplicated: boolean;
    end: any;
    job: Job;
    address: string;
    job_type: string;
    documents: any[];
    culmex_job_id: string;
    vehicle_id: string;
    start_date_time?: SvelteDate;
    end_date_time?: SvelteDate;
    status: string;
    original_job_id: string | null;
    is_task: boolean;
    is_employee: boolean;
    offset: number;
    customers: any[];
    postcode: string | null;
} = (job, vehicles) => {
    let startDateTime = null;
    //console.log("job.start_start_time", job.start_date_time);
    if (job.start_date_time !== null) {
        startDateTime = new SvelteDate(job.start_date_time);
    }
    let endDateTime = null;
    if (job.end_date_time !== null) {
        endDateTime = new SvelteDate(job.end_date_time);
    }

    // Calculate color and hue based on job type if applicable
    const jobTypeColor = getColorForJobType(job.job_type);
    const jobTypeHue = getHueForJobType(job.job_type);
    const finalHue = jobTypeHue !== null ? jobTypeHue : job.hue;
    const finalColor = jobTypeColor !== null ? jobTypeColor : 'gray';

    // If job has no vehicle but has a start date, assign to the "unassigned" vehicle
    let assignedVehicle = job.vehicle;
    let assignedSite = vehicles.find((s: { id: string }) => s.id === job.vehicle?.id) ?? null;

    if (!job.vehicle && startDateTime) {
        // Find the "unassigned" virtual vehicle
        const unassignedVehicle = vehicles.find((v: { id: string }) => v.id === 'unassigned');
        if (unassignedVehicle) {
            assignedVehicle = unassignedVehicle as any;
            assignedSite = unassignedVehicle;
        }
    }

    // $inspect(job, "job", "sites", sites);

    //console.log("Loading job", job, "with start date", startDateTime);
    const toReturn: {
        id: string;
        title: string;
        site: any;
        start: any;
        duration: number;
        modified: boolean;
        hue: number;
        notes: string;
        description: string;
        site_name: any;
        related_job_ids: string[];
        vehicle: Vehicle;
        employeeAssignments: SchedularEmployeeAssignment[];
        duplicated: boolean;
        end: any;
        job: Job;
        address: string;
        job_type: string;
        documents: any[];
        culmex_job_id: string;
        job_phase_id: string | null;
        job_phase: { id: string; label: string; sequence: number } | null;
        vehicle_id: string;
        start_date_time: SvelteDate | undefined;
        end_date_time: SvelteDate | undefined;
    } = $state({
        id: job.id,
        title: job.title,
        site: assignedSite,
        vehicle: assignedVehicle,
        start: startDateTime,
        duration: job.duration,
        modified: false,
        hue: finalHue,
        color: finalColor,
        notes: job.notes,
        description: job.notes ?? '',
        site_name: job.site_name,
        related_job_ids: job.related_job_ids ?? [],
        duplicated: job.duplicated,
        employeeAssignments: job.employeeAssignments ?? [],
        end: endDateTime,
        job: job.job,
        address: job.address,
        job_type: job.job_type,
        documents: job.documents ?? [],
        culmex_job_id: job.culmex_job_id,
        job_phase_id: job.job_phase_id ?? null,
        job_phase: job.job_phase ?? null,
        vehicle_id: job.vehicle_id,
        start_date_time: startDateTime,
        end_date_time: endDateTime,
        invoiced: job.invoiced ?? false,
        invoiced_date: job.invoiced_date ?? null,
        weekend_overrides: Array.isArray((job as { weekend_overrides?: unknown }).weekend_overrides)
            ? (job as { weekend_overrides: { weekend_start: string; include_saturday: boolean; include_sunday: boolean }[] }).weekend_overrides
            : [],
    });
    return toReturn;
};

export const buildRelatedJobsMap = (jobsMap: JobsMap, relatedJobMap: RelatedJobMap) => {
    for (const job of jobsMap.values()) {
        let set: Set<string> = jobGetDirectSiblings(job);
        set.add(job.id);
        for (const relId of set) {
            const relIds = relatedJobMap.get(relId);
            if (relIds !== undefined) {
                set = set.union(relIds);
            }
        }
        for (const relId of set) relatedJobMap.set(relId, set);
    }
    return relatedJobMap;
};

const jobGetDirectSiblings = (job: Job) => new Set(job.related_job_ids);

export const loadSiteVisit = (siteVisit: any, vehicles: SchedularVehicles): SchedularSiteVisit => {
    let startDateTime = null;
    if (siteVisit.start_date_time !== null) {
        startDateTime = new SvelteDate(siteVisit.start_date_time);
    }
    let endDateTime = null;
    if (siteVisit.end_date_time !== null) {
        endDateTime = new SvelteDate(siteVisit.end_date_time);
    }

    // Find the assigned vehicle or use unassigned virtual vehicle
    let assignedVehicle = siteVisit.vehicle;
    let assignedSite = vehicles.find((v) => v.id === siteVisit.vehicle?.id) ?? null;

    if (!siteVisit.vehicle && startDateTime) {
        // Site visits without a vehicle land in the dedicated "site-visits" row at the top
        const siteVisitsRow = vehicles.find((v) => v.id === 'site-visits');
        if (siteVisitsRow) {
            assignedVehicle = siteVisitsRow as any;
            assignedSite = siteVisitsRow;
        }
    }

    const toReturn: SchedularSiteVisit = $state({
        id: siteVisit.id,
        visitable_id: siteVisit.visitable_id,
        visitable_type: siteVisit.visitable_type,
        title: siteVisit.title,
        purpose: siteVisit.purpose,
        status: siteVisit.status,
        start: startDateTime,
        end: endDateTime,
        start_date_time: startDateTime,
        end_date_time: endDateTime,
        notes: siteVisit.notes,
        findings: siteVisit.findings,
        duration: siteVisit.duration || 14400, // 4 hours default
        hue: siteVisit.hue || 210, // Blue for site visits
        site_name: siteVisit.site_name,
        employeeAssignments: siteVisit.employeeAssignments || [],
        modified: false,
        offset: 0,
        visitable: siteVisit.visitable,
        job: siteVisit.visitable, // Alias for compatibility with vehicle assignments
        vehicle: assignedVehicle,
        site: assignedSite,
    });

    return toReturn;
};
